import React, { PureComponent, ReactNode } from 'react'
import { DispatchProp } from 'react-redux'
import { AnyAction } from 'redux'
import { connectRedux } from '../../helpers/connectRedux'
import { appsLanguagePositionSelected } from '../../helpers/app'
import { StorageService } from '../../services'
import literalsService, { Language } from '../../services/literals-service'
import { setChangeLanguageAction } from '../../store/actions'
import { IStoreState } from '../../store/states'
import StyledDiv from './styles'

interface IProps extends DispatchProp<AnyAction> {
  language: string
}

interface IState {
  isOpen: boolean
}

class LanguageSelector extends PureComponent<IProps, IState> {

  private container = React.createRef<HTMLDivElement>()

  private pill = React.createRef<HTMLButtonElement>()

  constructor(props: IProps) {
    super(props)

    this.state = { isOpen: false }
  }

  private get languages(): string[] {
    return appsLanguagePositionSelected?.languages ?? []
  }

  private get chevronIcon(): ReactNode {
    return (
      <svg className={ `chevron ${this.state.isOpen ? 'open' : ''}` } viewBox="0 0 24 25" aria-hidden="true">
        <path fill="currentColor" d="M20.2083 6.46413L22 8.2558L12 18.2558L2 8.29746L3.79167 6.5058L12 14.7141L20.2083 6.46413Z" />
      </svg>
    )
  }

  private renderOption = (option: string): ReactNode => {
    const isSelected = option === this.props.language

    return (
      <li key={ `language-option-${option}` }>
        <button
          type="button"
          className={ `option ${isSelected ? 'selected' : ''}` }
          aria-current={ isSelected }
          lang={ literalsService.getLanguageCode(option) }
          value={ option }
          onClick={ this.handleLanguagePress }
        >
          <span className="code" aria-hidden="true">{ literalsService.getLanguageCode(option) }</span>
          <span>{ literalsService.getInLanguage(option, option, true) }</span>
        </button>
      </li>
    )
  }

  public componentDidMount(): void {
    document.addEventListener('mousedown', this.handleClickOutside)
    document.addEventListener('keydown', this.handleEscape)
  }

  public componentWillUnmount(): void {
    document.removeEventListener('mousedown', this.handleClickOutside)
    document.removeEventListener('keydown', this.handleEscape)
  }

  public render(): ReactNode {
    const { language } = this.props
    const { isOpen } = this.state

    if (!appsLanguagePositionSelected?.showSelector || this.languages.length < 2) return null

    const languageName = literalsService.getInLanguage(language, language, true)

    return (
      <StyledDiv ref={ this.container }>
        <button
          ref={ this.pill }
          type="button"
          className="pill"
          onClick={ this.toggle }
          aria-expanded={ isOpen }
          aria-controls="language-options"
          aria-label={ `${ literalsService.get('changeLanguage', true) }: ${ languageName }` }
        >
          <span className="code">{ literalsService.getLanguageCode(language) }</span>
          { this.chevronIcon }
        </button>
        { isOpen && <ul id="language-options" className="options">{ this.languages.map(this.renderOption) }</ul> }
      </StyledDiv>
    )
  }

  private toggle = (): void => {
    this.setState(previousState => ({ isOpen: !previousState.isOpen }))
  }

  private handleClickOutside = (event: MouseEvent): void => {
    if (!this.state.isOpen) return
    if (this.container.current?.contains(event.target as Node)) return

    this.setState({ isOpen: false })
  }

  private handleEscape = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || !this.state.isOpen) return

    this.setState({ isOpen: false })
    this.pill.current?.focus()
  }

  private handleLanguagePress = (event: React.MouseEvent<HTMLButtonElement>): void => {
    const language = event.currentTarget.value

    literalsService.setLanguage(language)
    StorageService.setLanguage(language as Language)
    this.props.dispatch(setChangeLanguageAction(language))
    this.setState({ isOpen: false })
    this.pill.current?.focus()
  }
}

const mapStateToProps = ({ configuration }: IStoreState) => ({
  language: configuration.language
})

export default connectRedux(mapStateToProps, LanguageSelector)
